import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@roger/ui-components';
import { BookOpen, Loader2, Megaphone, Plus, Image } from 'lucide-react';
import { getAgencies, listPermissions } from '../../services/api';
import type {
  AgencyResponse,
  PermissionResponse,
} from '../../services/api/types.gen';
import { FeatureFlag } from '../../constants/featureFlags';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  deleteProductUpdate,
  listAllProductUpdates,
  PRODUCT_UPDATE_NONE_FEATURE_FLAG,
  PRODUCT_UPDATE_NONE_PERMISSION,
  ProductUpdatePriority,
  productUpdateAudienceLabel,
  productUpdatePriorityFromNumber,
  productUpdateTargetLabel,
  isProductUpdateExpired,
  upsertProductUpdate,
  type ProductUpdateImageResponse,
  type ProductUpdateManagementResponse,
} from '../../services/productUpdates';
import {
  ProductUpdateEditorForm,
  type EditorState,
  type MarkdownEditorView,
} from './ProductUpdateEditorForm';
import { ProductUpdateListRow } from './ProductUpdateListRow';
import { ProductUpdatesImageManager } from './ProductUpdatesImageManager';

const emptyEditor = (): EditorState => ({
  mode: 'create',
  version: '',
  markdown: '',
  permissionSlug: PRODUCT_UPDATE_NONE_PERMISSION,
  featureFlagSlug: PRODUCT_UPDATE_NONE_FEATURE_FLAG,
  agencyIds: [],
  priority: ProductUpdatePriority.Default,
  expiresAt: null,
});

const editorFromRow = (row: ProductUpdateManagementResponse): EditorState => ({
  mode: 'edit',
  id: row.id,
  version: row.version,
  markdown: row.markdown,
  permissionSlug: row.permission_slug ?? PRODUCT_UPDATE_NONE_PERMISSION,
  featureFlagSlug: row.feature_flag_slug ?? PRODUCT_UPDATE_NONE_FEATURE_FLAG,
  agencyIds: row.agency_ids ?? [],
  priority: productUpdatePriorityFromNumber(row.priority),
  expiresAt: row.expires_at ?? null,
});

const permissionSlugFromSelectValue = (value: string): string | null =>
  value === PRODUCT_UPDATE_NONE_PERMISSION ? null : value;

const featureFlagSlugFromSelectValue = (value: string): string | null =>
  value === PRODUCT_UPDATE_NONE_FEATURE_FLAG ? null : value;

const matchesUiSearch = (query: string, ...texts: string[]): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  return texts.some(text => text.toLowerCase().includes(needle));
};

type MarkdownCheatSheetEntry = {
  label: string;
  syntax: string;
};

type MarkdownCheatSheetSection = {
  heading: string;
  entries: MarkdownCheatSheetEntry[];
  note?: string;
};

const PRODUCT_UPDATES_MARKDOWN_CHEAT_SHEET_SECTIONS: MarkdownCheatSheetSection[] =
  [
    {
      heading: 'Headings',
      entries: [
        { label: 'Heading 1', syntax: '# Heading 1' },
        { label: 'Heading 2', syntax: '## Heading 2' },
        { label: 'Heading 3', syntax: '### Heading 3' },
      ],
    },
    {
      heading: 'Text',
      entries: [
        { label: 'Bold', syntax: '**bold text**' },
        { label: 'Italic', syntax: '*italic text*' },
      ],
    },
    {
      heading: 'Lists',
      entries: [
        {
          label: 'Bullet list',
          syntax: '- First item\n- Second item',
        },
        {
          label: 'Numbered list',
          syntax: '1. First item\n2. Second item',
        },
      ],
    },
    {
      heading: 'Links and quotes',
      entries: [
        { label: 'Link', syntax: '[link text](https://example.com)' },
        { label: 'Blockquote', syntax: '> Quoted text' },
      ],
    },
    {
      heading: 'Code',
      entries: [
        { label: 'Inline code', syntax: '`code`' },
        {
          label: 'Code block',
          syntax: '```\ncode block\n```',
        },
      ],
    },
    {
      heading: 'Divider',
      entries: [{ label: '', syntax: '---' }],
    },
    {
      heading: 'Images',
      entries: [
        {
          label: '',
          syntax: '![alt text](file_name)',
        },
      ],
      note: 'Upload the image with Images (use the file name without extension). Copy the markdown reference from Images, or type the syntax above.',
    },
  ];

type ProductUpdatesMarkdownCheatSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const ProductUpdatesMarkdownCheatSheet: React.FC<
  ProductUpdatesMarkdownCheatSheetProps
> = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='flex max-h-[85vh] max-w-lg flex-col overflow-hidden'>
      <DialogHeader className='shrink-0'>
        <DialogTitle>Markdown cheat sheet</DialogTitle>
      </DialogHeader>
      <p className='shrink-0 text-sm text-muted-foreground'>
        Type these patterns in the markdown editor. Preview shows how they
        render.
      </p>
      <div className='min-h-0 flex-1 space-y-5 overflow-y-auto pr-1 text-sm'>
        {PRODUCT_UPDATES_MARKDOWN_CHEAT_SHEET_SECTIONS.map(section => (
          <section key={section.heading}>
            <h3 className='mb-2 font-semibold'>{section.heading}</h3>
            <ul className='space-y-3'>
              {section.entries.map(entry => (
                <li key={entry.label}>
                  <span className='text-muted-foreground'>{entry.label}</span>
                  <pre className='mt-1 overflow-x-auto whitespace-pre-wrap rounded-md border bg-muted/50 px-3 py-2 font-mono text-xs'>
                    {entry.syntax}
                  </pre>
                </li>
              ))}
            </ul>
            {section.note ? (
              <p className='mt-2 text-xs text-muted-foreground'>
                {section.note}
              </p>
            ) : null}
          </section>
        ))}
      </div>
      <DialogFooter className='shrink-0 sm:justify-end'>
        <Button size='sm' variant='outline' onClick={() => onOpenChange(false)}>
          Close
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const ProductUpdatesManagement: React.FC = () => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );

  const [updates, setUpdates] = useState<ProductUpdateManagementResponse[]>([]);
  const [images, setImages] = useState<ProductUpdateImageResponse[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [permissions, setPermissions] = useState<PermissionResponse[]>([]);
  const [agencies, setAgencies] = useState<AgencyResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [markdownEditorView, setMarkdownEditorView] =
    useState<MarkdownEditorView>('text');
  const [deleteTarget, setDeleteTarget] =
    useState<ProductUpdateManagementResponse | null>(null);
  const [imagesDialogOpen, setImagesDialogOpen] = useState(false);
  const [cheatSheetOpen, setCheatSheetOpen] = useState(false);
  const [updatesSearchQuery, setUpdatesSearchQuery] = useState('');

  useEffect(() => {
    setMarkdownEditorView('text');
  }, [editor?.id, editor?.mode]);

  const syncEditorFromUpdates = useCallback(
    (rows: ProductUpdateManagementResponse[]) => {
      setEditor(prev => {
        if (!prev?.id) {
          return prev;
        }
        const row = rows.find(r => r.id === prev.id);
        return row ? editorFromRow(row) : prev;
      });
    },
    []
  );

  const selectablePermissions = useMemo(
    () => permissions.filter(p => !p.is_internal),
    [permissions]
  );

  const permissionSelectOptions = useMemo(
    () => [
      { value: PRODUCT_UPDATE_NONE_PERMISSION, label: 'All Users' },
      ...selectablePermissions.map(perm => ({
        value: perm.slug,
        label: perm.name,
      })),
    ],
    [selectablePermissions]
  );

  const featureFlagSelectOptions = useMemo(
    () => [
      { value: PRODUCT_UPDATE_NONE_FEATURE_FLAG, label: 'All feature flags' },
      ...Object.values(FeatureFlag).map(flag => ({
        value: flag,
        label: flag,
      })),
    ],
    []
  );

  const agencySelectOptions = useMemo(
    () =>
      agencies
        .map(agency => ({
          value: agency.id,
          label: agency.written_name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [agencies]
  );

  const permissionSlugToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const perm of permissions) {
      map.set(perm.slug, perm.name);
    }
    return map;
  }, [permissions]);

  const agencyIdToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const agency of agencies) {
      map.set(agency.id, agency.written_name);
    }
    return map;
  }, [agencies]);

  const loadPermissions = useCallback(async () => {
    try {
      const res = await listPermissions({
        query: { limit: 500 },
        throwOnError: true,
      });
      setPermissions(res.data ?? []);
    } catch (error) {
      console.error('Failed to load permissions:', error);
      showError(
        'Failed to Load Permissions',
        'Unable to load permissions. Please try again.'
      );
    }
  }, [showError]);

  const loadAgencies = useCallback(async () => {
    try {
      const res = await getAgencies({
        query: { limit: 1000 },
        throwOnError: true,
      });
      setAgencies(res.data ?? []);
    } catch (error) {
      console.error('Failed to load agencies:', error);
      showError(
        'Failed to Load Agencies',
        'Unable to load agencies. Please try again.'
      );
    }
  }, [showError]);

  const loadUpdates = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listAllProductUpdates();
      setUpdates(data.updates);
      setImages(data.images ?? []);
      setImageUrls(data.image_urls ?? {});
      syncEditorFromUpdates(data.updates);
    } catch (error) {
      console.error('Failed to load product updates:', error);
      setUpdates([]);
      setImages([]);
      setImageUrls({});
      showError(
        'Unable to Load Product Updates',
        'Something went wrong. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [showError, syncEditorFromUpdates]);

  useEffect(() => {
    void loadPermissions();
    void loadAgencies();
    void loadUpdates();
  }, [loadAgencies, loadPermissions, loadUpdates]);

  const handleSave = async () => {
    if (!editor) {
      return;
    }
    const version = editor.version.trim();
    const markdown = editor.markdown.trim();
    if (!version || !markdown) {
      showError(
        'Missing Fields',
        'Version, markdown, and priority are required.'
      );
      return;
    }

    setIsSaving(true);
    try {
      await upsertProductUpdate({
        id: editor.mode === 'edit' ? editor.id : undefined,
        version,
        markdown,
        permission_slug: permissionSlugFromSelectValue(editor.permissionSlug),
        feature_flag_slug: featureFlagSlugFromSelectValue(
          editor.featureFlagSlug
        ),
        agency_ids: editor.agencyIds,
        priority: editor.priority,
        expires_at: editor.expiresAt,
      });
      showSuccess(
        editor.mode === 'create'
          ? 'Product Update Created'
          : 'Product Update Saved',
        editor.mode === 'create'
          ? 'The new product update entry was saved.'
          : 'Your changes were saved.'
      );
      setEditor(null);
      await loadUpdates();
    } catch (error) {
      console.error('Failed to save product update:', error);
      showError(
        'Save Failed',
        'Unable to save product update. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteProductUpdate(deleteTarget.id);
      showSuccess(
        'Product Update Deleted',
        'The product update entry was removed.'
      );
      if (editor?.mode === 'edit' && editor.id === deleteTarget.id) {
        setEditor(null);
      }
      setDeleteTarget(null);
      await loadUpdates();
    } catch (error) {
      console.error('Failed to delete product update:', error);
      showError(
        'Delete Failed',
        'Unable to delete product update. Please try again.'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const isFormValid =
    editor !== null &&
    editor.version.trim() !== '' &&
    editor.markdown.trim() !== '';

  const filteredUpdates = useMemo(() => {
    return updates.filter(row =>
      matchesUiSearch(
        updatesSearchQuery,
        row.version,
        productUpdateAudienceLabel(row.permission_slug, permissionSlugToName),
        productUpdateTargetLabel(row, permissionSlugToName, agencyIdToName)
      )
    );
  }, [agencyIdToName, updates, updatesSearchQuery, permissionSlugToName]);

  const editorFormProps =
    editor === null
      ? null
      : {
          editor,
          setEditor,
          markdownEditorView,
          setMarkdownEditorView,
          permissionSelectOptions,
          featureFlagSelectOptions,
          agencySelectOptions,
          permissionSlugToName,
          agencyIdToName,
          imageUrls,
          isFormValid,
          isSaving,
          onSave: () => void handleSave(),
          onCancel: () => setEditor(null),
        };

  return (
    <section>
      <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <p className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Megaphone className='h-4 w-4 shrink-0' />
          Manage ordered changelog content shown in the product update modal.
          Target entries by permission, WorkOS feature flag, or agency. Use the
          Image Library to upload assets referenced in markdown.
        </p>
        <div className='flex shrink-0 flex-wrap gap-2'>
          <Button
            size='sm'
            variant='outline'
            onClick={() => setCheatSheetOpen(true)}
          >
            <BookOpen className='h-4 w-4 mr-1' />
            Markdown help
          </Button>
          <Button
            size='sm'
            variant='outline'
            onClick={() => setImagesDialogOpen(true)}
          >
            <Image className='h-4 w-4 mr-1' />
            Images
            {images.length > 0 ? ` (${images.length})` : ''}
          </Button>
          <Button
            size='sm'
            onClick={() => setEditor(emptyEditor())}
            disabled={editor !== null}
          >
            <Plus className='h-4 w-4 mr-1' />
            Add Product Line
          </Button>
        </div>
      </div>
      <div className='space-y-6'>
        <ProductUpdatesMarkdownCheatSheet
          open={cheatSheetOpen}
          onOpenChange={setCheatSheetOpen}
        />

        <Dialog open={imagesDialogOpen} onOpenChange={setImagesDialogOpen}>
          <DialogContent className='flex max-h-[85vh] max-w-lg flex-col overflow-hidden'>
            <DialogHeader className='shrink-0'>
              <DialogTitle>Image Library</DialogTitle>
            </DialogHeader>
            <div className='flex min-h-[280px] flex-1 flex-col overflow-hidden'>
              <ProductUpdatesImageManager
                images={images}
                imageUrls={imageUrls}
                updates={updates}
                permissionSlugToName={permissionSlugToName}
                onImagesChanged={loadUpdates}
                editorResetKey={`${editor ? `${editor.mode}-${editor.id ?? 'new'}` : 'closed'}-${imagesDialogOpen}`}
              />
            </div>
          </DialogContent>
        </Dialog>

        {editor?.mode === 'create' && editorFormProps && (
          <div className='rounded-lg border bg-muted/30 p-4'>
            <ProductUpdateEditorForm {...editorFormProps} />
          </div>
        )}

        {isLoading ? (
          <div className='flex items-center gap-2 text-muted-foreground text-sm'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading product updates…
          </div>
        ) : updates.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No product updates yet. Add one to show changelog content in the
            app.
          </p>
        ) : (
          <div className='space-y-3'>
            <Input
              inputSize='sm'
              value={updatesSearchQuery}
              onChange={e => setUpdatesSearchQuery(e.target.value)}
              placeholder='Search by version or audience…'
              aria-label='Search product updates'
            />
            {filteredUpdates.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No product updates match your search.
              </p>
            ) : (
              <ul className='flex flex-col gap-3'>
                {filteredUpdates.map(row => {
                  const isEditingRow =
                    editor?.mode === 'edit' && editor.id === row.id;

                  if (isEditingRow && editorFormProps) {
                    const rowExpired = isProductUpdateExpired(row.expires_at);
                    return (
                      <li
                        key={row.id}
                        className={
                          rowExpired
                            ? 'rounded-md border border-destructive bg-destructive/5 p-4'
                            : 'rounded-md border bg-muted/30 p-4'
                        }
                      >
                        <ProductUpdateEditorForm {...editorFormProps} />
                      </li>
                    );
                  }

                  return (
                    <ProductUpdateListRow
                      key={row.id}
                      row={row}
                      permissionSlugToName={permissionSlugToName}
                      agencyIdToName={agencyIdToName}
                      imageUrls={imageUrls}
                      editorOpen={editor !== null}
                      isDeleting={isDeleting}
                      onEdit={() => setEditor(editorFromRow(row))}
                      onDelete={() => setDeleteTarget(row)}
                    />
                  );
                })}
              </ul>
            )}
          </div>
        )}

        <Dialog
          open={deleteTarget !== null}
          onOpenChange={open => !open && setDeleteTarget(null)}
        >
          <DialogContent className='max-w-sm'>
            <DialogHeader>
              <DialogTitle>Delete product update</DialogTitle>
            </DialogHeader>
            <div className='py-2'>
              <p className='text-sm text-muted-foreground'>
                Delete the update for{' '}
                <span className='font-medium text-foreground'>
                  {deleteTarget
                    ? productUpdateTargetLabel(
                        deleteTarget,
                        permissionSlugToName,
                        agencyIdToName
                      )
                    : ''}
                </span>{' '}
                (version{' '}
                <span className='font-medium text-foreground'>
                  {deleteTarget?.version}
                </span>
                )? This cannot be undone.
              </p>
            </div>
            <DialogFooter>
              <Button
                variant='outline'
                onClick={() => setDeleteTarget(null)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                onClick={() => void handleConfirmDelete()}
                disabled={isDeleting}
              >
                {isDeleting && (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' />
                )}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default ProductUpdatesManagement;
